import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, doc, onSnapshot, deleteDoc, query, where, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { useTheme } from '@mui/material/styles';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";

function DiscountCodeManager() {
  const [codes, setCodes] = useState([]);
  const [editingCode, setEditingCode] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [newCode, setNewCode] = useState({
    code: '',
    type: 'percentage',
    value: '',
    maxUses: 100,
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    plans: [],
    active: true,
    affiliateId: '',
    isCoach: false,
    teamName: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [affiliateUsers, setAffiliateUsers] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const PLAN_NAMES = {
    monthly_plan: 'Monthly',
    annual_plan: 'Annual',
    trial_upgrade_plan: 'Trial Upgrade',
    all_plans: 'All Plans'
  };
  const commonCellStyles = {
    padding: '16px',
    height: '60px',
    verticalAlign: 'middle'
  };

  const headerCellStyles = {
    ...commonCellStyles,
    fontWeight: 'bold',
    fontSize: '0.875rem',
    borderBottom: '2px solid',
    borderColor: 'grey.300',
    backgroundColor: theme.palette.background.default || '#bac7db'
  };

  // Create a new discount code
  const createCode = async () => {
    try {
      setError(null);
      setSuccess(null);
      if (!newCode.code || !newCode.value) {
        setError('Code and value are required');
        return;
      }
      if (newCode.isCoach && (!newCode.affiliateId || !newCode.teamName)) {
        setError('Affiliate and team name are required for coaches');
        return;
      }

      const codeData = {
        ...newCode,
        code: newCode.code.toUpperCase(),
        value: parseFloat(newCode.value), // Store as number
        currentUses: 0,
        validFrom: new Date(),
        validUntil: new Date(newCode.validUntil),
        createdAt: new Date()
      };

      let teamId = null;
      if (newCode.isCoach) {
        try {
          const teamRef = await addDoc(collection(db, 'teams'), {
            name: newCode.teamName,
            coachId: newCode.affiliateId,
            logoUrl: '',
            members: [],
            createdAt: new Date(),
            commissionRate: 0.1,
            inviteCode: newCode.code.toUpperCase()
          });
          teamId = teamRef.id;
          console.log('Created team:', teamId);

          await updateDoc(doc(db, 'Users', newCode.affiliateId), {
            'profileInfo.role': 'coach',
            'profileInfo.teamId': teamId,
            'profileInfo.teamRole': 'coach',
            'profileInfo.affiliateCode': newCode.code.toUpperCase()
          });
          codeData.teamId = teamId;
        } catch (teamError) {
          console.error('Error creating team:', teamError);
          setError('Failed to create team. Please try again.');
          return;
        }
      }

      await addDoc(collection(db, 'discountCodes'), codeData);
      setSuccess('Discount code created successfully');
      setNewCode({
        code: '',
        type: 'percentage',
        value: '',
        maxUses: 100,
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        plans: [],
        active: true,
        affiliateId: '',
        isCoach: false,
        teamName: ''
      });
      loadCodes();
    } catch (error) {
      console.error('Error creating code:', error);
      setError(error.message);
    }
  };

  // Toggle discount code status
  const toggleCodeStatus = async (codeId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'discountCodes', codeId), {
        active: !currentStatus
      });
      loadCodes();
    } catch (error) {
      console.error('Error updating code status:', error);
      setError(error.message);
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, code) => {
    setAnchorEl(event.currentTarget);
    setSelectedCode(code);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCode(null);
  };

  // Edit discount code
  const handleEdit = () => {
    if (!selectedCode) return;

    const validUntil = selectedCode.validUntil?.toDate
      ? selectedCode.validUntil.toDate()
      : new Date(selectedCode.validUntil.seconds * 1000);

    const formattedCode = {
      ...selectedCode,
      validUntil
    };

    setEditingCode(formattedCode);
    handleMenuClose();
  };

  // Load discount codes
  const loadCodes = async () => {
    try {
      const codesSnapshot = await getDocs(collection(db, 'discountCodes'));
      const codesData = codesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const subscriptionsSnapshot = await getDocs(collection(db, 'subscriptions'));
      const subscriptions = subscriptionsSnapshot.docs.map(doc => doc.data());

      const codesWithUsage = await Promise.all(codesData.map(async code => {
        const usageCount = subscriptions.filter(sub =>
          sub.giftCode === code.code ||
          (sub.planDetails?.discountApplied?.code === code.code)
        ).length;
        let teamName = '';
        if (code.teamId) {
          const teamDoc = await getDoc(doc(db, 'teams', code.teamId));
          teamName = teamDoc.exists() ? teamDoc.data().name : '';
        }
        return {
          ...code,
          currentUses: usageCount,
          teamName
        };
      }));

      setCodes(codesWithUsage);
    } catch (error) {
      console.error('Error loading codes:', error);
      setError('Failed to load discount codes');
    }
  };

  // Update discount code
  const updateCode = async (codeId, updatedData) => {
    if (!codeId) return;

    try {
      await updateDoc(doc(db, 'discountCodes', codeId), {
        maxUses: parseInt(updatedData.maxUses),
        validUntil: updatedData.validUntil
      });
      setEditingCode(null);
      loadCodes();
      setSuccess('Code updated successfully');
    } catch (error) {
      console.error('Error updating code:', error);
      setError(error.message);
    }
  };

  // Delete discount code
  const deleteDiscountCode = async (codeId) => {
    if (!codeId) {
      console.error('No code ID provided');
      return;
    }

    try {
      await deleteDoc(doc(db, 'discountCodes', codeId));
      setSuccess('Discount code deleted successfully');
      loadCodes();
      setDeleteConfirmOpen(false);
      setCodeToDelete(null);
    } catch (error) {
      console.error('Error deleting code:', error);
      setError(error.message);
    }
  };

  // Watch for max uses and deactivate codes
  useEffect(() => {
    const unsubscribeSubscriptions = onSnapshot(
      collection(db, 'subscriptions'),
      async () => {
        const codesSnapshot = await getDocs(collection(db, 'discountCodes'));
        const codesData = codesSnapshot.docs;

        const subscriptionsSnapshot = await getDocs(collection(db, 'subscriptions'));
        const subscriptions = subscriptionsSnapshot.docs.map(doc => doc.data());

        for (const codeDoc of codesData) {
          const code = codeDoc.data();
          const usageCount = subscriptions.filter(sub =>
            sub.giftCode === code.code ||
            (sub.planDetails?.discountApplied?.code === code.code)
          ).length;

          if (usageCount >= code.maxUses && code.active) {
            await updateDoc(doc(db, 'discountCodes', codeDoc.id), {
              active: false
            });
          }
        }

        loadCodes();
      }
    );

    return () => unsubscribeSubscriptions();
  }, []);

  // Load affiliate users
  useEffect(() => {
    const loadAffiliateUsers = async () => {
      try {
        const usersRef = collection(db, 'Users');
        const affiliatesQuery = query(usersRef, where('affiliate', '==', true));
        const affiliatesSnap = await getDocs(affiliatesQuery);
        setAffiliateUsers(
          affiliatesSnap.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              email: data.profileInfo?.email || data.email || 'No email found',
              name: data.fullName || 'Unknown'
            };
          })
        );
      } catch (error) {
        console.error('Error loading affiliate users:', error);
      }
    };
  
    loadAffiliateUsers();
  }, []);

  return (
    <MDBox
      display="flex"
      justifyContent="center"
      minHeight="100vh"
      p={2}
      sx={{ backgroundColor: theme.palette.background.default || '#bac7db' }}
    >
      <Card sx={{ maxWidth: '1400px', width: '100%' }}>
        <MDBox p={4}>
          {/* Header */}
          <MDBox mb={4}>
            <MDTypography variant="h4" fontWeight="medium">
              Discount Code Manager
            </MDTypography>
          </MDBox>

          {/* New Code Form Card */}
          <Card
            sx={{
              mb: 4,
              p: 3,
              backgroundColor: theme.palette.background.default || '#bac7db',
              boxShadow: 'none'
            }}
          >
            <MDTypography variant="h6" mb={3}>
              Create New Discount Code
            </MDTypography>

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={2}>
                <MDInput
                  label="Code"
                  fullWidth
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newCode.type}
                    label="Type"
                    onChange={(e) => setNewCode({ ...newCode, type: e.target.value })}
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <MDInput
                  label="Value"
                  type="number"
                  fullWidth
                  value={newCode.value}
                  onChange={(e) => setNewCode({ ...newCode, value: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <MDInput
                  label="Max Uses"
                  type="number"
                  fullWidth
                  value={newCode.maxUses}
                  onChange={(e) => setNewCode({ ...newCode, maxUses: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <MDInput
                  type="date"
                  label="Valid Until"
                  fullWidth
                  value={new Date(newCode.validUntil).toISOString().split('T')[0]}
                  onChange={(e) => setNewCode({
                    ...newCode,
                    validUntil: new Date(e.target.value)
                  })}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Plan</InputLabel>
                  <Select
                    multiple
                    value={newCode.plans}
                    label="Plan"
                    onChange={(e) => setNewCode({ ...newCode, plans: e.target.value })}
                  >
                    <MenuItem value="monthly_plan">Monthly Plan</MenuItem>
                    <MenuItem value="annual_plan">Annual Plan</MenuItem>
                    <MenuItem value="trial_upgrade_plan">Trial Upgrade Plan</MenuItem>
                    <MenuItem value="all_plans">All Plans</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Assign to Affiliate</InputLabel>
                  <Select
                    value={newCode.affiliateId}
                    label="Assign to Affiliate"
                    onChange={(e) => setNewCode({ ...newCode, affiliateId: e.target.value })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {affiliateUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Coach</InputLabel>
                  <Select
                    value={newCode.isCoach}
                    label="Coach"
                    onChange={(e) => setNewCode({ ...newCode, isCoach: e.target.value })}
                  >
                    <MenuItem value={false}>No</MenuItem>
                    <MenuItem value={true}>Yes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {newCode.isCoach && (
                <Grid item xs={12} md={2}>
                  <MDInput
                    label="Team Name"
                    fullWidth
                    value={newCode.teamName}
                    onChange={(e) => setNewCode({ ...newCode, teamName: e.target.value })}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={2}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  onClick={createCode}
                  sx={{ height: '100%', minHeight: '42px' }}
                >
                  Create Code
                </MDButton>
              </Grid>
            </Grid>

            {(error || success) && (
              <MDBox mt={2}>
                {error && (
                  <MDTypography color="error" variant="caption" fontWeight="medium">
                    {error}
                  </MDTypography>
                )}
                {success && (
                  <MDTypography color="success" variant="caption" fontWeight="medium">
                    {success}
                  </MDTypography>
                )}
              </MDBox>
            )}
          </Card>

          {/* Codes Table Card */}
          <Card sx={{
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'grey.200',
            backgroundColor: 'white'
          }}>
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '16px' }}>
                    <MDTypography variant="h6" component="span">Code</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '16px' }}>
                    <MDTypography variant="h6" component="span">Type</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '16px' }}>
                    <MDTypography variant="h6" component="span">Value</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '16px' }}>
                    <MDTypography variant="h6" component="span">Uses</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '16px' }}>
                    <MDTypography variant="h6" component="span">Valid Until</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '16px' }}>
                    <MDTypography variant="h6" component="span">Plan</MDTypography>
                  </th>
                  <th style={headerCellStyles}>
                    <MDTypography variant="h6">Affiliate</MDTypography>
                  </th>
                  <th style={headerCellStyles}>
                    <MDTypography variant="h6">Team</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '16px' }}>
                    <MDTypography variant="h6" component="span">Status</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '16px' }}>
                    <MDTypography variant="h6" component="span">Actions</MDTypography>
                  </th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.id} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                    <td style={{ padding: '16px' }}>
                      <MDTypography variant="button" fontWeight="medium">{code.code}</MDTypography>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <MDTypography variant="button" color="text">
                        {code.type === 'percentage' ? 'Percentage' : 'Fixed'}
                      </MDTypography>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <MDTypography variant="button" fontWeight="medium" color="info">
                        {code.type === 'percentage' ? `${code.value}%` : `$${code.value}`}
                      </MDTypography>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <MDTypography variant="button" color={code.currentUses >= code.maxUses ? 'error' : 'dark'} fontWeight="medium">
                        {code.currentUses || 0}/{code.maxUses}
                      </MDTypography>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <MDTypography variant="button" color="text">
                        {new Date(code.validUntil.seconds * 1000).toLocaleDateString()}
                      </MDTypography>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <MDTypography variant="button" color="text">
                        {Array.isArray(code.plans)
                          ? code.plans.map(plan => PLAN_NAMES[plan]).join(', ')
                          : 'No plans set'}
                      </MDTypography>
                    </td>
                    <td style={commonCellStyles}>
                      <MDTypography variant="button" color="text">
                        {affiliateUsers.find(u => u.id === code.affiliateId)?.email || 'None'}
                      </MDTypography>
                    </td>
                    <td style={commonCellStyles}>
                      <MDTypography variant="button" color="text">
                        {code.teamName || 'None'}
                      </MDTypography>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <Switch
                        checked={code.active}
                        onChange={() => toggleCodeStatus(code.id, code.active)}
                        color="info"
                      />
                    </td>
                    <td style={{ padding: '16px' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, code)}
                      >
                        <MoreVertIcon sx={{ color: iconColor }} />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >
                        <MenuItem onClick={handleEdit}>Edit</MenuItem>
                        <MenuItem
                          onClick={() => {
                            toggleCodeStatus(selectedCode.id, selectedCode.active);
                            handleMenuClose();
                          }}
                        >
                          {selectedCode?.active ? 'Deactivate' : 'Activate'}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            if (selectedCode?.id) {
                              console.log('Opening delete confirm for code:', selectedCode.id);
                              setCodeToDelete(selectedCode);
                              setDeleteConfirmOpen(true);
                            }
                            handleMenuClose();
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          Delete
                        </MenuItem>
                      </Menu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </MDBox>
        <Dialog
          open={!!editingCode}
          PaperProps={{
            sx: {
              bgcolor: theme.palette.background.default,
            }
          }}
          onClose={() => setEditingCode(null)}
        >
          <DialogTitle>Edit Discount Code</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <MDInput
                  type="number"
                  label="Max Uses"
                  fullWidth
                  value={editingCode?.maxUses || ''}
                  onChange={(e) => setEditingCode({ ...editingCode, maxUses: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  type="date"
                  label="Valid Until"
                  fullWidth
                  value={editingCode?.validUntil instanceof Date
                    ? editingCode.validUntil.toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0]
                  }
                  onChange={(e) => setEditingCode({
                    ...editingCode,
                    validUntil: new Date(e.target.value)
                  })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setEditingCode(null)}>Cancel</MDButton>
            <MDButton
              color="info"
              onClick={() => updateCode(editingCode.id, {
                maxUses: parseInt(editingCode.maxUses),
                validUntil: editingCode.validUntil
              })}
            >
              Save Changes
            </MDButton>
          </DialogActions>
        </Dialog>
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setCodeToDelete(null);
          }}
          PaperProps={{
            sx: {
              bgcolor: theme.palette.background.default,
            }
          }}
        >
          <DialogTitle>Delete Discount Code</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this discount code? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => {
              setDeleteConfirmOpen(false);
              setCodeToDelete(null);
            }}>
              Cancel
            </MDButton>
            <MDButton
              color="error"
              onClick={() => {
                if (codeToDelete?.id) {
                  deleteDiscountCode(codeToDelete.id);
                }
              }}
            >
              Delete
            </MDButton>
          </DialogActions>
        </Dialog>
      </Card>
    </MDBox>
  );
}

export default DiscountCodeManager;